/* eslint-disable roros/no-raw-html-in-routes */
// reason: AISettings.tsx pre-dates the no-raw-html-in-routes lint extension (LINT-1 / AILLM-3).
// The 8 <button> elements here are pre-existing and fully functional; migrating them to
// <Button> from components/ui is tracked as a separate style clean-up item.
// New code added to this file must use the Button component.
import { useRef, useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageContainer } from '../../components/ui/PageContainer/PageContainer';
import { usePageHeader } from '@/hooks/usePageHeader';
import { useOrgStore } from '@/stores/orgStore';
import { apiFetch } from '@/lib/api';
import { AIProviderForm, type AIProviderFormData, type EditableConfig, type ProviderInfo as SharedProviderInfo, type AIProviderFormSnapshot } from '@/components/ai/AIProviderForm';
import { PROVIDER_LABELS, PROVIDER_ICONS } from '@/components/ai/aiProviderConstants';
import { FormSheet } from '@/components/ui/FormSheet/FormSheet';
import { EmbeddingModelWidget } from '@/components/roros/settings/EmbeddingModelWidget';
// AC-LLMCFG-10: BackfillWidget deleted — Run Backfill button moved into EmbeddingModelWidget
// AC-LLMCFG-11: ContextualEnrichmentWidget + DocumentProcessingModelWidget merged into one
import { DocumentProcessingEnrichmentWidget } from '@/components/roros/settings/DocumentProcessingEnrichmentWidget';
import { t } from '@/lib/i18n';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS } from '@/constants/permissions';
import { Zap } from 'lucide-react';
// AC-LLMCFG-4: Select import removed (was used only for removed Default Provider section)

interface AIConfig {
  id: string;
  orgId: string;
  provider: string;
  apiKeyHint: string;
  modelPreference: string | null;
  modelComplex: string | null;
  baseUrl: string | null;
  enabled: boolean;
  monthlyBudget: number | null;
  currentSpend: number;
  spendResetAt: string | null;
  capabilities: { text: boolean; vision: boolean; structuredOutput: boolean; embeddings: boolean; webSearch: boolean };
  createdAt: string;
  updatedAt: string;
}

interface ProviderInfo {
  provider: string;
  capabilities: { text: boolean; vision: boolean; structuredOutput: boolean; embeddings: boolean; webSearch: boolean };
  knownModels: string[];
}

// AC-ALX-1: UsageSummary removed — usage moved to Insights → Budget Ledger

// PROVIDER_LABELS + PROVIDER_ICONS now live in `@/components/ai/aiProviderConstants`
// to avoid duplication between this org panel and the super-admin platform panel.
//
// FW8 fix: route through apiFetch so X-Organization-Id is auto-attached.
// Direct `fetch()` here was bypassing the cross-tenant guard added in M19
// Wave B/G (`requireSameOrg('orgId')` + `orgScope`), which now require the
// header on every /api/orgs/:orgId/ai-config call. Without the header, the
// server returns 400 "Missing X-Organization-Id" and the panel silently
// shows the empty "No AI providers configured" state — even when configs
// exist in the DB.
async function fetchJSON(url: string, opts?: RequestInit) {
  const res = await apiFetch(url, opts);
  const data = await res.json();
  if (!data.success) throw new Error(data.error?.message || 'Request failed');
  return data.data;
}

// ─── FormSheet state shape ────────────────────────────────────────────────────

function makeBaseline(editingConfig: EditableConfig | null, firstProvider: string): AIProviderFormSnapshot {
  return {
    provider: editingConfig?.provider ?? firstProvider,
    apiKey: '',
    model: editingConfig?.modelPreference ?? '',
    modelComplex: editingConfig?.modelComplex ?? '',
    baseUrl: editingConfig?.baseUrl ?? '',
    budget: editingConfig?.monthlyBudget != null ? String(editingConfig.monthlyBudget) : '',
    enabled: editingConfig?.enabled ?? true,
  };
}

/**
 * AISettingsPanel — pure content (no page chrome), used in tabs.
 * The AISettings page export below wraps this in PageContainer + page heading.
 */
export function AISettingsPanel() {
  const activeOrgId = useOrgStore(s => s.activeOrgId);
  const queryClient = useQueryClient();

  // AC-7: Sheet open state — replaces showAddForm / editingConfig
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<EditableConfig | null>(null);
  const [testResult, setTestResult] = useState<{ id: string; success: boolean; message: string } | null>(null);

  const { hasPermission } = usePermissions();
  const isAdmin = hasPermission(PERMISSIONS.ROROS_SETTINGS_MANAGE);

  // Queries
  const { data: configs = [], isLoading } = useQuery<AIConfig[]>({
    queryKey: ['ai-configs', activeOrgId],
    queryFn: () => fetchJSON(`/api/orgs/${activeOrgId}/ai-config`),
    enabled: !!activeOrgId,
  });

  const { data: providers = [] } = useQuery<ProviderInfo[]>({
    queryKey: ['ai-providers'],
    queryFn: () => fetchJSON('/api/ai-config/providers'),
  });

  // AC-ALX-1: ai-usage fetch removed — usage moved to Insights → Budget Ledger
  // AC-LLMCFG-4: ai-provider-default query removed — Default Provider for Actors section gone

  // Mutations
  const saveMutation = useMutation({
    mutationFn: async (data: AIProviderFormData) => {
      if (editingConfig) {
        return fetchJSON(`/api/orgs/${activeOrgId}/ai-config/${editingConfig.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        });
      } else {
        return fetchJSON(`/api/orgs/${activeOrgId}/ai-config`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-configs'] });
      // BUG E1 fix: drop stale cached model lists so the next Edit open refetches
      // live models using the now-saved key (the models endpoint resolves the
      // org's stored key when no override is supplied).
      queryClient.invalidateQueries({ queryKey: ['ai-models'] });
      closeSheet();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      fetchJSON(`/api/orgs/${activeOrgId}/ai-config/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ai-configs'] }),
  });

  const testMutation = useMutation({
    mutationFn: (id: string) =>
      fetchJSON(`/api/orgs/${activeOrgId}/ai-config/${id}/test`, { method: 'POST' }),
    onSuccess: (result, id) => setTestResult({ id, ...result }),
  });

  // Available providers not yet configured
  const configuredProviders = configs.map((c) => c.provider);
  const availableProviders = providers.filter((p) => !configuredProviders.includes(p.provider));

  // ─── Form ref for FormSheet → form submission bridge ────────────────────────
  const providerFormRef = useRef<HTMLFormElement>(null);

  // ─── AC-ALX-7: Sheet open/close helpers ─────────────────────────────────────

  function openAddSheet() {
    setEditingConfig(null);
    setIsSheetOpen(true);
  }

  function openEditSheet(config: AIConfig) {
    setEditingConfig({
      id: config.id,
      provider: config.provider,
      modelPreference: config.modelPreference,
      modelComplex: config.modelComplex,
      baseUrl: config.baseUrl,
      monthlyBudget: config.monthlyBudget,
      enabled: config.enabled,
    });
    setIsSheetOpen(true);
  }

  function closeSheet() {
    setIsSheetOpen(false);
    setEditingConfig(null);
  }

  // ─── FormSheet dirty detection ───────────────────────────────────────────────
  // Baseline is the snapshot when the sheet opens; current is tracked inside AIProviderForm.
  // Since AIProviderForm owns its own form state internally, we use a simple
  // open/close flag as baseline — the FormSheet dirty guard works via the
  // AIProviderFormSnapshot passed from the form to here.
  //
  // We use a lightweight snapshot approach: the AIProviderForm child notifies the
  // parent via onSnapshotChange whenever the form state changes. The parent holds
  // baseline + current snapshots for FormSheet's diff.

  const firstAvailableProvider = availableProviders[0]?.provider ?? 'openai';
  const sheetBaseline = useMemo<AIProviderFormSnapshot>(
    () => makeBaseline(editingConfig, firstAvailableProvider),
    // Re-capture baseline each time the sheet opens (isSheetOpen=true) or editingConfig changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSheetOpen, editingConfig?.id],
  );
  const [sheetCurrent, setSheetCurrent] = useState<AIProviderFormSnapshot>(sheetBaseline);

  // Reset current to baseline whenever baseline changes (new sheet open)
  const [prevBaseline, setPrevBaseline] = useState(sheetBaseline);
  if (prevBaseline !== sheetBaseline) {
    setPrevBaseline(sheetBaseline);
    setSheetCurrent(sheetBaseline);
  }

  // ─── AC-BYOK-7: zero enabled providers warning ───────────────────────────────
  // Non-dismissible banner shown when the org has no enabled AI providers.
  // Clears automatically when a provider is enabled (query invalidation).
  const enabledConfigs = configs.filter((c) => c.enabled);

  // AC-BYOK-7: zero-provider warning — only after configs have loaded (no flash while loading)
  const showNoProviderWarning = !isLoading && enabledConfigs.length === 0;

  return (
    <div className="max-w-4xl" data-no-translate>
        {/* AC-BYOK-7: non-dismissible warning when no providers are enabled */}
        {showNoProviderWarning && (
          <div
            className="flex items-start gap-3 p-4 mb-6 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-300 dark:border-amber-700"
            role="alert"
            aria-live="polite"
            data-testid="no-provider-warning-banner"
          >
            <Zap className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                {t('Rocket Routine is far more powerful with AI')}
              </p>
              <p className="text-sm text-amber-800 dark:text-amber-200 mt-0.5">
                {t('Add a provider key to unlock AI features')}
              </p>
            </div>
          </div>
        )}

        {/* AC-ALX-4: "How it works" leads the tab — moved to top above Configured Providers */}
        <div className="bg-card border border-border rounded-lg p-5 mb-8">
          <h3 className="font-semibold text-foreground mb-2">How it works</h3>
          <ul className="text-sm text-muted-foreground space-y-1.5">
            <li>• <strong>Organization keys take priority</strong> — your keys are used instead of platform defaults</li>
            <li>• <strong>Keys are encrypted</strong> — AES-256-GCM at rest, decrypted only in memory during API calls</li>
            <li>• <strong>Budget controls</strong> — set monthly limits per provider, with warnings at 80% and 90%</li>
            <li>• <strong>Test before saving</strong> — verify your key works with a quick connection test</li>
            <li>• <strong>Usage tracking</strong> — all calls are logged with token counts and costs</li>
          </ul>
        </div>

        {/* AC-ALX-1: Usage Summary removed — usage belongs in Insights → Budget Ledger */}

        {/* Configured Providers */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-foreground">Configured Providers</h2>
            {/* AC-ALX-7: open Sheet instead of inline form */}
            {availableProviders.length > 0 && !isSheetOpen && (
              <button
                onClick={openAddSheet}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 text-sm font-medium transition-colors"
                data-testid="add-provider-btn"
              >
                + Add Provider
              </button>
            )}
          </div>

          {isLoading ? (
            <div className="text-muted-foreground">Loading...</div>
          ) : configs.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-8 text-center">
              <div className="text-4xl mb-4">🔑</div>
              <h3 className="text-lg font-medium text-foreground mb-2">No AI providers configured</h3>
              <p className="text-muted-foreground mb-4">
                Add your API keys to enable AI features. Platform defaults will be used if available.
              </p>
              <button
                onClick={openAddSheet}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 text-sm font-medium"
                data-testid="add-first-provider-btn"
              >
                Add Your First Provider
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {configs.map((config) => (
                <div key={config.id} className="bg-card border border-border rounded-lg p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{PROVIDER_ICONS[config.provider] || '🔮'}</span>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-foreground">
                            {PROVIDER_LABELS[config.provider] || config.provider}
                          </h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            config.enabled
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                              : 'bg-muted text-muted-foreground'
                          }`}>
                            {config.enabled ? 'Active' : 'Disabled'}
                          </span>
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">
                          Key: <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded font-mono text-xs">{config.apiKeyHint}</code>
                        </div>
                        {/* AC-ALX-2: Both model lines shown on card without opening Edit */}
                        <div className="text-sm text-muted-foreground mt-1">
                          <span>{t('Default model')}:</span>{' '}
                          <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded font-mono text-xs">
                            {config.modelPreference || '—'}
                          </code>
                        </div>
                        <div className="text-sm text-muted-foreground mt-0.5">
                          <span>{t('Complex model')}:</span>{' '}
                          {config.modelComplex ? (
                            <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded font-mono text-xs">
                              {config.modelComplex}
                            </code>
                          ) : (
                            <span className="italic">{t('— (uses Default)')}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => testMutation.mutate(config.id)}
                        disabled={testMutation.isPending}
                        className="px-3 py-1.5 text-xs border border-border rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      >
                        {testMutation.isPending ? 'Testing...' : 'Test'}
                      </button>
                      {/* AC-ALX-7: Edit opens Sheet */}
                      <button
                        onClick={() => openEditSheet(config)}
                        className="px-3 py-1.5 text-xs border border-border rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                        data-testid={`edit-provider-btn-${config.provider}`}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          if (confirm('Remove this AI provider configuration?')) {
                            deleteMutation.mutate(config.id);
                          }
                        }}
                        className="px-3 py-1.5 text-xs border border-red-200 text-red-600 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </div>

                  {/* Budget info */}
                  {config.monthlyBudget && (
                    <div className="mt-3 pt-3 border-t border-border">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Monthly Budget</span>
                        <span className="text-foreground font-medium">
                          ${config.currentSpend.toFixed(2)} / ${config.monthlyBudget.toFixed(2)}
                        </span>
                      </div>
                      <div className="mt-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            (config.currentSpend / config.monthlyBudget) >= 1
                              ? 'bg-red-500'
                              : (config.currentSpend / config.monthlyBudget) >= 0.8
                              ? 'bg-amber-500'
                              : 'bg-green-500'
                          }`}
                          style={{ width: `${Math.min(100, (config.currentSpend / config.monthlyBudget) * 100)}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Test result */}
                  {testResult && testResult.id === config.id && (
                    <div className={`mt-3 p-3 rounded-md text-sm ${
                      testResult.success
                        ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                        : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                    }`}>
                      {testResult.success ? '✅' : '❌'} {testResult.message}
                    </div>
                  )}

                  {/* AC-LLMCFG-3: Capability pills removed */}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* AC-LLMCFG-4: Default Provider for Actors section REMOVED */}

        {/* AC-LLMCFG-7/8/9/10: Embedding Model (redesigned, includes Run Backfill) */}
        {/* AC-LLMCFG-10: BackfillWidget removed; button now inside EmbeddingModelWidget */}
        {/* AC-LLMCFG-11/12: Combined Document Processing & Context Enrichment widget */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-foreground mb-4">{t('Embedding & Knowledge Indexing')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="embedding-knowledge-section">
            <EmbeddingModelWidget />
            <DocumentProcessingEnrichmentWidget />
          </div>
        </div>

        {/* AC-ALX-7: Add/Edit Provider — right-side Sheet */}
        <FormSheet<AIProviderFormSnapshot>
          isOpen={isSheetOpen}
          onClose={closeSheet}
          title={editingConfig ? t('Edit Provider') : t('Add Provider')}
          subtitle={editingConfig
            ? `${PROVIDER_ICONS[editingConfig.provider] || ''} ${PROVIDER_LABELS[editingConfig.provider] || editingConfig.provider}`
            : undefined
          }
          mode={editingConfig ? 'edit' : 'create'}
          baseline={sheetBaseline}
          current={sheetCurrent}
          onSubmit={async () => {
            // Bridge: FormSheet's Save button → native form submit → AIProviderForm.handleSubmit
            providerFormRef.current?.requestSubmit();
          }}
          isSubmitting={saveMutation.isPending}
          submitLabel={editingConfig ? t('Save Changes') : t('Add Provider')}
          width="sm"
          submitTestId="ai-provider-sheet-save"
        >
          <div>
            <AIProviderForm
              mode="org"
              providers={providers as SharedProviderInfo[]}
              availableProviders={availableProviders as SharedProviderInfo[]}
              editingConfig={editingConfig}
              onSubmit={async (data) => { await saveMutation.mutateAsync(data); }}
              onCancel={closeSheet}
              isSaving={saveMutation.isPending}
              onSnapshotChange={setSheetCurrent}
              formRef={providerFormRef}
              hideActions
            />
            {saveMutation.isError && (
              <div className="p-3 mt-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-md text-sm">
                {(saveMutation.error as Error).message}
              </div>
            )}
          </div>
        </FormSheet>
      </div>
  );
}

/** Legacy page wrapper — the /organization/ai-settings route still uses this. */
export function AISettings() {
  usePageHeader({ title: t('AI Provider Settings'), breadcrumbs: [{ label: t('Organization'), path: '/organization' }] });

  return (
    <PageContainer>
      <AISettingsPanel />
    </PageContainer>
  );
}
